<!--@format-->
<template>
    <div class="social-post-template-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ options.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div v-if="currentStep === 'summary-post-review'">
                        <social-post-review-step
                            :is-templates="true"
                            :is-from-studio="isFromStudio"
                            :post="getPostReviewDetails"
                            @go-to-step="goToStep($event)"
                            @close-widget="closeWidget()"
                            @unschedule="unschedulePost()"
                        />
                    </div>
                    <div
                        v-if="currentStep == 'starting-point'"
                        class="create-social-post-step starting-point"
                    >
                        <social-post-starting-point-step
                            v-model="postCreationSteps['starting-point'].next"
                            :start-options="startOptionsForSocialMedia"
                        />
                    </div>
                    <div
                        v-if="
                            currentStep == 'post-fields' ||
                            currentStep == 'edit-post-info' ||
                            currentStep === 'previously-sent-post-edit' ||
                            currentStep === 'existing-template-edit'
                        "
                        class="create-social-post-step"
                    >
                        <social-post-fields-step
                            :v="$v"
                            :is-templates="true"
                            :post="post"
                            :step="currentStep"
                            :account-id="accountId"
                            :categories-selected="getCategories"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="currentStep == 'post-fields' || currentStep === 'edit-post-info'"
                        class="back-btn"
                        @click="goBack()"
                    >
                        Back
                    </app-button>
                    <app-button
                        v-if="
                            currentStep === 'starting-point' ||
                            currentStep === 'post-fields' ||
                            currentStep === 'previously-sent-post-edit' ||
                            currentStep === 'existing-template-edit' ||
                            (currentStep === 'summary-post-review' && isBuilderView)
                        "
                        class="save-btn"
                        @click="nextStep()"
                    >
                        {{ currentStep === 'summary-post-review' ? 'Finish' : 'Next' }}
                    </app-button>
                    <app-button
                        v-if="currentStep === 'edit-post-info'"
                        class="save-btn"
                        @click="saveEdit()"
                    >
                        Save
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { SocialMediaPostsMixin } from '@/mixins/social-media-posts-mixin';
import { SocialMediaPostTemplatesMixin } from '@/mixins/social-media-post-templates-mixin';
import { required } from 'vuelidate/lib/validators';
import EventBus from '@/scripts/event-bus.js';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const SocialPostReviewStep = () =>
    import('@/components/sliding-widgets/social-post/social-post-review-step');
const SocialPostStartingPointStep = () =>
    import('@/components/sliding-widgets/social-post/social-post-starting-point-step');
const SocialPostFieldsStep = () =>
    import('@/components/sliding-widgets/social-post/social-post-fields-step');

export default {
    name: 'social-post-template-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'social-post-review-step': SocialPostReviewStep,
        'social-post-starting-point-step': SocialPostStartingPointStep,
        'social-post-fields-step': SocialPostFieldsStep,
        'loading-screen-svg': LoadingScreenSvg,
    },

    mixins: [SocialMediaPostsMixin, SocialMediaPostTemplatesMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default() {
                return {
                    title: 'Create Post Template',
                };
            },
        },
    },

    data() {
        return {
            isLoading: false,
            currentStep: 'starting-point',
            post: {
                id: 1,
                name: null,
                categories: [],
            },

            originalPost: {},
            postReviewDetails: {},

            postCreationSteps: {
                'starting-point': {
                    next: 'post-fields',
                    previous: '',
                },

                'post-fields': {
                    previous: 'starting-point',
                    next: 'send-to-create-post-view',
                },

                'previously-sent-post-edit': {
                    previous: 'starting-point',
                    next: 'send-to-create-from-previous-post',
                },

                'existing-template-edit': {
                    previous: 'starting-point',
                    next: 'send-to-create-from-existing-template',
                },

                'summary-post-review': {
                    previous: '',
                    next: 'finish',
                },

                'edit-post-info': {
                    previous: 'summary-post-review',
                    next: '',
                },
            },
        };
    },

    validations() {
        return {
            post: {
                name: {
                    required,
                },

                categories: {
                    required: false,
                },
            },
        };
    },

    computed: {
        clientId() {
            return this.widget.options.clientId;
        },

        accountId() {
            return this.widget.options.accountId;
        },

        getCategories() {
            return this.post.categories;
        },

        isEditMode() {
            return this.widget.templateId !== null && this.widget.templateId !== undefined;
        },

        isBuilderView() {
            return this.widget.isBuilderView !== null && this.widget.isBuilderView !== undefined
                ? this.widget.isBuilderView
                : false;
        },

        getPostReviewDetails() {
            return this.postReviewDetails;
        },

        isFormValid() {
            return (
                (this.currentStep !== 'post-fields' && this.currentStep !== 'edit-post-info') ||
                !this.$v.post.$invalid
            );
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        isFromStudio() {
            return this.$route.query.fromStudio === 'true' || this.widget.options.isFromStudio;
        },

        startOptionsForSocialMedia() {
            const options = [
                {
                    value: 'existing-template-edit',
                    name: 'Existing Template',
                    imageUrl: require('@/assets/images/social-posts/create-from-previous.png'),
                },
                {
                    value: 'post-fields',
                    name: 'Start Fresh',
                    imageUrl: require('@/assets/images/social-posts/create-new.png'),
                },
            ];

            if (this.clientId) {
                options.unshift({
                    value: 'previously-sent-post-edit',
                    name: 'Previously Sent Post',
                    imageUrl: require('@/assets/images/social-posts/create-from-previous.png'),
                });
            }

            return options;
        },
    },

    created() {
        if (this.isEditMode) {
            this.getPostTemplateToEdit();
        }

        if (this.widget.step) {
            this.currentStep = this.widget.step;
        }

        if (this.widget.actualData) {
            this.post.name = this.widget.actualData.name;
            this.post.categories = this.widget.actualData.categories.split(',');
        }

        EventBus.$on('social-post-select', this.getPreviouslyPost);
        EventBus.$on('post-template-select', this.getPostTemplate);
    },

    beforeDestroy() {
        EventBus.$off('social-post-select', this.getPreviouslyPost);
        EventBus.$off('post-template-select', this.getPostTemplate);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        refreshEventTemplateMainPage() {
            EventBus.$emit('social-post-templates:update');
        },

        goToStep(step) {
            this.currentStep = step;
        },

        validateFields() {
            this.$v.post.name.$touch();
            this.$v.post.categories.$touch();
        },

        nextStep() {
            if (!this.isFormValid) {
                this.validateFields();
                return;
            }

            this.currentStep = this.postCreationSteps[this.currentStep].next;

            if (this.currentStep === 'previously-sent-post-edit') {
                this.openChoosePreviousPostWidget();
            }

            if (this.currentStep === 'existing-template-edit') {
                this.openChoosePostTemplateWidget();
            }

            if (this.currentStep === 'send-to-create-post-view') {
                this.sendToCreatePostView();
            }

            if (this.currentStep === 'send-to-create-from-previous-post') {
                this.sendToCreateATemplateFromPreviousPost();
            }

            if (this.currentStep === 'send-to-create-from-existing-template') {
                this.sendToCreateATemplateFromExistingTemplate();
            }

            if (this.currentStep === 'finish') {
                this.sendToPostTemplatesBoard();
            }
        },

        sendToCreatePostView() {
            const dest =
                `${this.baseUrl}/SocialMedias/PostTemplates/Create?` +
                this.serialize({
                    name: this.post.name,
                    categories: this.getCategories.join(),
                });
            this.$router.push(dest).then(() => {
                this.closeWidget();
            });
        },

        sendToCreateATemplateFromPreviousPost() {
            const dest =
                `${this.baseUrl}/SocialMedias/PostTemplates/Create?` +
                this.serialize({
                    name: this.post.name,
                    categories: this.getCategories.join(),
                    previouspost: this.post.id,
                });

            this.$router.push(dest).then(() => {
                this.closeWidget();
            });
        },

        sendToCreateATemplateFromExistingTemplate() {
            const dest =
                `${this.baseUrl}/SocialMedias/PostTemplates/Create?` +
                this.serialize({
                    name: this.post.name,
                    categories: this.getCategories.join(),
                    postTemplate: this.post.id,
                });

            this.$router.push(dest).then(() => {
                this.closeWidget();
            });
        },

        sendToPostTemplatesBoard() {
            const dest = this.isFromStudio
                ? `${this.baseUrl}/DesignStudio/MyCreations`
                : `${this.baseUrl}/SocialMedias/PostTemplates`;

            this.$router.push(dest).then(() => {
                this.closeWidget();
            });
        },

        sendPostDataToBuilderView() {
            EventBus.$emit('update-social-post-info', this.post);
            this.closeWidget();
        },

        openChoosePreviousPostWidget() {
            const widget = {
                name: 'social-post-select-widget',
                options: {
                    clientId: this.clientId,
                },
            };

            this.currentStep = 'starting-point';
            EventBus.$emit('open-widget', widget);
        },

        openChoosePostTemplateWidget() {
            const widget = {
                name: 'social-post-select-widget',
                options: {
                    accountId: this.accountId,
                    clientId: this.clientId,
                    isTemplates: true,
                },
            };

            this.currentStep = 'starting-point';
            EventBus.$emit('open-widget', widget);
        },

        getPreviouslyPost(item) {
            this.isLoading = true;
            this.getPostDetails(item.id, this.clientId)
                .then((response) => {
                    this.post = {
                        id: response.Id,
                        name: response.PostName,
                        categories: response.Categories,
                    };

                    this.currentStep = 'previously-sent-post-edit';
                })
                .catch((error) => {
                    this.currentStep = 'starting-point';
                    this.$helpers.showFailNotification('Failed to get social post.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getPostTemplate(item) {
            this.isLoading = true;
            this.getPostTemplateDetails(item.id, this.accountId)
                .then((response) => {
                    this.post = {
                        id: response.Id,
                        name: response.TemplateName,
                        categories: response.Categories,
                    };

                    this.currentStep = 'existing-template-edit';
                })
                .catch((error) => {
                    this.currentStep = 'starting-point';
                    this.$helpers.showFailNotification('Failed to get social post template.', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getPostTemplateToEdit() {
            this.isLoading = true;

            const { templateId } = this.widget;

            this.getPostTemplateDetails(templateId, this.accountId)
                .then((response) => {
                    this.post = {
                        id: response.Id,
                        name: response.TemplateName,
                        categories: response.Categories,
                    };
                    this.originalPost = {
                        id: response.Id,
                        name: response.TemplateName,
                        categories: JSON.parse(JSON.stringify(response.Categories)),
                    };

                    this.postReviewDetails = {
                        id: response.Id,
                        categories: JSON.parse(JSON.stringify(response.Categories)),
                        postText: response.TemplateText,
                        postMediaData: response.SocialPostTemplateMedia,
                        pageShareUrl: response.PageShareUrl,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get social post template.', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        saveEdit() {
            if (!this.isFormValid) {
                this.validateFields();
                return;
            }

            if (this.isBuilderView) {
                this.sendPostDataToBuilderView();
            } else {
                this.saveDraftInfo();
            }
        },

        saveDraftInfo() {
            if (this.isEqualObject(this.post, this.originalPost)) {
                return;
            }
            this.isLoading = true;
            const { templateId } = this.widget;

            const postPayload = {
                templateName: this.post.name,
                templateCategories: this.post.categories,
            };

            this.updateWidgetPostTemplateDraft(templateId, this.accountId, postPayload)
                .then(() => {
                    this.$helpers.showSuccessNotification('Your post template has been updated.');
                    this.getPostTemplateToEdit();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update your post template.', {
                        error,
                    });
                })
                .finally(() => {
                    this.refreshEventTemplateMainPage();
                    this.closeWidget();
                    this.isLoading = false;
                });
        },

        isEqualObject(obj1, obj2) {
            return JSON.stringify(obj1) === JSON.stringify(obj2);
        },

        goBack() {
            this.currentStep =
                this.postCreationSteps[this.currentStep].previous !== ''
                    ? this.postCreationSteps[this.currentStep].previous
                    : 'starting-point';
        },

        serialize(obj) {
            var queryString = Object.keys(obj)
                .map((key) => {
                    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
                })
                .join('&');

            return queryString;
        },
    },
};
</script>

<style>
.social-post-template-widget .widget-contents-container .content-wrapper .loading-view {
    display: flex;
    justify-content: center;
    align-items: center;
}

.social-post-template-widget .widget-contents-container .loading-screen {
    margin-left: 0px;
}

.social-post-template-widget .widget-contents-container .widget-footer .action-container {
    display: flex;
}

.social-post-template-widget .widget-contents-container .widget-footer .action-container .back-btn {
    margin-right: 15px;
    background: transparent;
    color: #18aeff;
}

.social-post-template-widget .widget-contents-container .widget-footer .action-container .btn {
    margin-left: 15px;
}

.social-post-template-widget .field-container .lists {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.social-post-template-widget .field-container .lists .list {
    font-size: 11px;
    padding: 0px 7px;
    border: 1px solid #cac6cb;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.social-post-template-widget .field-container .lists .list .x {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.social-post-template-widget
    .field-container
    .tag-list-input-results
    .tag-list-input-result-item
    .x {
    margin-left: 12px;
    width: 10px;
    cursor: pointer;
}

.social-post-template-widget .field-container .lists .list .q23-linkedin-svg,
.social-post-template-widget .field-container .lists .list .p23-facebook-svg {
    width: 19px;
    margin-right: 6px;
}

.social-post-template-widget .field-container .lists .list .x path,
.social-post-template-widget
    .field-container
    .tag-list-input-results
    .tag-list-input-result-item
    .x
    .a {
    fill: #ee206f;
}

.social-post-template-widget .is-scheduled {
    display: flex;
    justify-content: space-between;
    max-width: 250px;
    align-items: center;
    margin: 0 auto;
}

.social-post-template-widget .is-scheduled label {
    display: flex;
    align-items: center;
    justify-content: center;
}

.social-post-template-widget .is-scheduled label .radio-schedule {
    appearance: none;
    border-radius: 50%;
    border: 5px solid #fff;
    outline: 2px solid #382153;
    height: 30px;
    width: 30px;
}

.social-post-template-widget .is-scheduled label input[type='radio'] {
    margin: 0px 10px 0px 0px;
}

.social-post-template-widget .is-scheduled label.selected .radio-schedule {
    background: #382153;
}

.social-post-template-widget .schedule-post-fieldset {
    padding: 15px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.social-post-template-widget .schedule-post-fieldset .date {
    width: 100%;
    margin-bottom: 10px;
}

.social-post-template-widget .schedule-post-fieldset .time-picker {
    margin-bottom: 10px;
}

.social-post-template-widget .schedule-post-fieldset .date .app-date-picker input {
    padding: 9px 15px !important;
}

.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .icon-container
    .calendar-svg
    path,
.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .icon-container
    .calendar-svg
    rect {
    fill: #f30070 !important;
}

.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .input-container:hover
    .icon-container
    .calendar-svg
    path,
.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .input-container:hover
    .icon-container
    .calendar-svg
    rect {
    fill: #f30070 !important;
}

.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .input-container:focus-within
    .icon-container
    .calendar-svg
    path,
.social-post-template-widget
    .schedule-post-fieldset
    .date
    .app-date-picker
    .input-container:focus-within
    .icon-container
    .calendar-svg
    rect {
    fill: #18aeff !important;
}

.social-post-template-widget .schedule-post-fieldset .time-picker .app-input input {
    text-align: center;
    padding: 9px 15px !important;
}

.social-post-template-widget .schedule-post-fieldset .time-picker .colon {
    padding-bottom: 8px !important;
    margin: 0 5px;
}

.social-post-template-widget .schedule-post-fieldset .time-picker .meridian {
    margin-left: 10px;
}

.social-post-template-widget .schedule-post-fieldset .time-picker .multiselect__tags {
    min-height: 41px !important;
}

.social-post-template-widget .schedule-post-fieldset .time .time-input {
    margin: 0 5px;
}

.social-post-template-widget .schedule-post-fieldset .time-input {
    max-width: 120px;
}

.social-post-template-widget .schedule-post-fieldset select {
    background: #fafafa;
    font-size: 1em;
    border: 2px solid #cac6cb;
    padding: 7px 15px;
    border-radius: 20px;
    color: #382153;
    outline: none;
    margin: 5px 0;
    flex-grow: 1;
}

.social-post-template-widget .schedule-post-fieldset .timezone {
    width: 100%;
}

.social-post-template-widget .loading-screen {
    text-align: center;
}

.social-post-template-widget .create-social-post-step.starting-point .title {
    display: block;
    margin: 0 auto;
    text-align: center;
    background-color: white;
    transform: translate(0, -50%);
    width: calc(100% - 40px);
    min-width: 280px;
}

.social-post-template-widget .create-social-post-step.schedule-point {
    border: 2px solid #e9e9ef;
    padding: 40px 30px 30px 30px;
    border-radius: 20px;
    position: relative;
}

.social-post-template-widget .create-social-post-step.schedule-point .title {
    position: absolute;
    top: -12px;
    left: 25%;
    font-size: 18px;
    font-weight: 600;
    color: #382153;
    background: #ffffff;
    display: block;
    padding: 0 30px;
    margin: 0;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option {
    width: 50%;
    padding: 15px;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option.--active
    img {
    border: 2px solid #18aeff !important;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label {
    display: block;
    width: 100%;
    text-align: center;
    cursor: pointer;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label:hover
    img {
    border: 2px solid #333;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label
    img {
    border: 2px solid transparent;
    border-radius: 10px;
    overflow: hidden;
    width: 100%;
    height: auto;
    margin-bottom: 15px;
    transition: all 0.3s ease-in-out;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label
    input,
.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label
    p {
    display: inline-block;
}

.social-post-template-widget
    .create-social-post-step.starting-point
    .create-social-post-options-list
    .social-post-option
    label
    input {
    margin-right: 0.5em;
    font-size: 22px;
}

.social-post-template-widget .post-name-container .post-name-label {
    font-weight: 500;
}

.social-post-template-widget .trigger-select-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.social-post-template-widget .list-trigger-container .add-btn {
    width: 100%;
    height: 39px;
    background-color: #18aeff;
    border-radius: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-top: 15px;
}

.social-post-template-widget .list-input-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 56px;
}

.social-post-template-widget .list-input-container .active-post-lists {
    text-align: center;
}

.social-post-template-widget .list-input-container .lists .list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #877d88;
    border-radius: 20px;
    font-size: 16px;
    color: #ffffff;
    padding: 11px 15px;
    margin-top: 11px;
}

.social-post-template-widget .list-input-container .lists .list .delete-list {
    cursor: pointer;
}

.social-post-template-widget .list-trigger-container .add-btn .plus-sign {
    height: 21px;
    width: 21px;
}

.social-post-template-widget .list-trigger-container .add-btn .plus-sign .default {
    fill: #ffffff;
}

.social-post-template-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.social-post-template-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.social-post-template-widget .field-container {
    margin-bottom: 23px;
}

.social-post-template-widget .trigger-select-container {
    margin-bottom: 30px;
}

.social-post-template-widget .trigger-label {
    position: relative;
    bottom: -12px;
    width: -webkit-fit-content;
    width: -moz-fit-content;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 58px;
}

.social-post-template-widget .list-label,
.social-post-template-widget .date-label {
    position: relative;
    bottom: -12px;
    width: -webkit-fit-content;
    width: -moz-fit-content;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 47px;
}

.social-post-template-widget .list-trigger-container {
    margin-bottom: 30px;
}

.social-post-template-widget .date-trigger-container .date-and-time-container {
    padding: 22px 56px;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.social-post-template-widget .date-trigger-container .date-and-time-container .hour,
.social-post-template-widget .date-trigger-container .date-and-time-container .minute,
.social-post-template-widget .date-trigger-container .date-and-time-container .time-multiselect,
.social-post-template-widget
    .date-trigger-container
    .date-and-time-container
    .time-zone-multiselect {
    width: 20px;
}

.social-post-template-widget .date-trigger-container .date-and-time-container .time-container {
    display: flex;
    justify-content: space-evenly;
}

.social-post-template-widget .timezone {
    width: 300px;
}

.social-post-template-widget .field-container .tag-list-input-results {
    display: flex;
    flex-wrap: wrap;
    margin-top: 7px;
}

.social-post-template-widget .field-container .tag-list-input-results .tag-list-input-result-item {
    margin-right: 0.5em;
    font-size: 11px;
    border: 1px #cac6cb solid;
    border-radius: 1.6em;
    padding: 0.5em 1em;
    max-width: 220px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: #000;
    display: flex;
    align-items: center;
}

.social-post-template-widget
    .field-container
    .tag-list-input-results
    .tag-list-input-result-item
    .delete-btn {
    position: -webkit-sticky;
    position: sticky;
    right: 0;
    font-size: 0.8em;
    margin-left: auto;
    color: red;
    font-weight: bold;
    margin-left: 10px;
    padding: 0.5em;
    width: 2.4em;
    height: 2.4em;
    text-align: center;
    border-radius: 10px;
}

.social-post-template-widget
    .field-container
    .tag-list-input-results
    .tag-list-input-result-item
    .delete-btn:hover {
    background-color: red;
    color: white;
}
</style>
